import React, {useState} from 'react';
import {useQuery} from 'react-query';
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import useMixById from '../../api/mixes/useMixById.ts';
import {MixModel} from '../../api/types.ts';
import tryParseGuid from '../../lib/tryParseGuid.ts';
import ProjectTabNames from '../projects/ProjectTabNames.ts';
import {projectTabSlots} from '../projects/tabs/projectTabSlots.ts';
import {Checkbox, RadioCard} from '../ds/FormField.tsx';
import {LoadingState} from '../ds/StateView.tsx';
import Icon from '../shared/Icon.tsx';
import {ProjectCardTarget} from './projectCard.ts';

interface ChooseProjectTabModalProps {
    /** The project's id or custom url. */
    projectRef: string;
    initialTarget?: ProjectCardTarget;
    confirmLabel?: string;
    onBack?: () => void;
    onClose: () => void;
    onChosen: (mix: MixModel, target: ProjectCardTarget) => void;
}

/*
 * Picks which tab a project card opens, and whether full screen. Fetches the
 * project so the list is its current tabs rather than whatever the card was
 * built from. Picking the first tab without full screen is the plain project
 * link, stored as no tab at all.
 */
const ChooseProjectTabModal: React.FC<ChooseProjectTabModalProps> = ({
    projectRef,
    initialTarget,
    confirmLabel = 'Add',
    onBack,
    onClose,
    onChosen
}) => {
    const mixId = tryParseGuid(projectRef);
    const fetchProject = useMixById({
        mixId,
        customUrl: mixId ? null : projectRef
    });
    const {data: mix, isLoading} = useQuery(
        ['project-card-tabs', projectRef],
        fetchProject,
        {cacheTime: 0}
    );

    const [tabName, setTabName] = useState(
        initialTarget?.tab ?? ProjectTabNames.Project
    );
    const [fullscreen, setFullscreen] = useState(
        initialTarget?.fullscreen ?? false
    );

    const slots = mix ? projectTabSlots(mix) : [];
    const selected = slots.find(s => s.name === tabName) ?? slots[0];
    const canFullscreen = selected?.canFullscreen ?? false;

    const onConfirm = () => {
        if (!mix || !selected) {
            return;
        }

        const goFullscreen = canFullscreen && fullscreen;
        const isPlainLink = selected.slot === 1 && !goFullscreen;

        onChosen(mix, {
            tab: isPlainLink ? null : selected.name,
            tabTitle: isPlainLink ? null : selected.title,
            fullscreen: goFullscreen
        });
    };

    return (
        <Modal isOpen toggle={onClose}>
            <ModalHeader toggle={onClose}>
                {mix ? `Open ${mix.name} at` : 'Open at'}
            </ModalHeader>
            <ModalBody>
                {isLoading ? (
                    <LoadingState variant="rows" />
                ) : !mix ? (
                    <p className="text-secondary m-0">
                        That project could not be found.
                    </p>
                ) : (
                    <>
                        <div role="radiogroup" aria-label="Tab">
                            {slots.map(slot => (
                                <RadioCard
                                    key={slot.slot}
                                    name="project-card-tab"
                                    label={
                                        <span className="d-inline-flex align-items-center gap-2">
                                            {slot.icon && (
                                                <Icon
                                                    icon={slot.icon}
                                                    size="xs"
                                                />
                                            )}
                                            {slot.title}
                                        </span>
                                    }
                                    checked={selected?.slot === slot.slot}
                                    onChange={() => setTabName(slot.name)}
                                />
                            ))}
                        </div>
                        <Checkbox
                            className="mt-3"
                            label="Open full screen"
                            description={
                                canFullscreen
                                    ? 'Fills the window with this tab.'
                                    : 'This kind of tab has no full screen view.'
                            }
                            checked={canFullscreen && fullscreen}
                            disabled={!canFullscreen}
                            onChange={e => setFullscreen(e.target.checked)}
                        />
                    </>
                )}
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between align-items-center">
                <Button color="secondary" onClick={onBack ?? onClose}>
                    {onBack ? 'Back' : 'Cancel'}
                </Button>
                <Button
                    color="primary"
                    disabled={!mix || !selected}
                    onClick={onConfirm}
                >
                    {confirmLabel}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ChooseProjectTabModal;
